import { BusinessUnit } from '@/types/common';

// Dash Rent pricing packages (Rent section).
//
// Field names mirror the mgmt API contract (`/rent/mgmt/v1/packages`) and the
// TRD §4.1 `package` table. Money is integer Rupiah; dates/times are WIB.

/** Contract type a package is sold under (TRD P7: the shared ContractType). */
export enum RentType {
  RENT = 'RENT',
  RTO = 'RTO',
}

export const RENT_TYPE_LABEL: Record<RentType, string> = {
  [RentType.RENT]: 'Rent',
  [RentType.RTO]: 'Rent To Own',
};

/**
 * Catalog lifecycle — no DRAFT; saving publishes. `name` is unique among
 * non-archived packages, so an archived name can be reused.
 */
export enum PackageStatus {
  ACTIVE = 'ACTIVE',
  /** Published but withheld from matching; still editable. */
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export const PACKAGE_STATUS_LABEL: Record<PackageStatus, string> = {
  [PackageStatus.ACTIVE]: 'Active',
  [PackageStatus.INACTIVE]: 'Inactive',
  [PackageStatus.ARCHIVED]: 'Archived',
};

/** PRIVATE: hidden from the driver catalogue, reached only via a referral. */
export enum PackageVisibility {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export const PACKAGE_VISIBILITY_LABEL: Record<PackageVisibility, string> = {
  [PackageVisibility.PUBLIC]: 'Publik',
  [PackageVisibility.PRIVATE]: 'Khusus',
};

/** `endAt` null only on a Default campaign; nullable so a stray one renders. */
export interface PackageHeldBy {
  campaignId: string;
  campaignName: string;
  startAt: string;
  endAt: string | null;
  status: 'draft' | 'active' | 'paused' | 'archived';
  /** Optional until coupon-service sends it through fleet. */
  referralCount?: number;
}

/** `null` = coupon-service did not answer: show "unknown", never "free". */
export type PackageHeldByList = PackageHeldBy[] | null;

/**
 * Recommended package artwork size — landscape, so the image reads as a
 * banner across the card rather than a badge beside the title. Off-spec
 * uploads are accepted; the control only warns.
 */
export const PACKAGE_IMAGE_DIMENSIONS = { width: 1024, height: 512 };

/**
 * Shared-upload `image_type` for package artwork. The upstream upload service
 * validates against a fixed enum with no `package` member, so this reuses the
 * value the model photo already uploads under. It only decides the temp-bucket
 * path prefix — the API relocates the object into `<packageID>/` on save.
 */
export const PACKAGE_IMAGE_UPLOAD_TYPE = 'vehicle';

/** Stand-in while a package has no artwork of its own. */
export const PACKAGE_IMAGE_PLACEHOLDER = '/images/dash-electric.png';

// ---------------------------------------------------------------------------
// Rule criteria (TRD §4.2)
// ---------------------------------------------------------------------------

/** Vehicle quality grade, a rule-matching input (TRD P3). */
export enum VehicleCondition {
  GOOD = 'GOOD',
  MINOR_DAMAGE = 'MINOR_DAMAGE',
  MAJOR_DAMAGE = 'MAJOR_DAMAGE',
}

/**
 * Versioned criteria object. `version` is required; the continuous dimensions
 * are min/max ranges — there is no operator interpreter on the backend — and
 * the rest are membership lists.
 */
export interface PackageRuleCriteria {
  version: 1;
  /** Kilometres. */
  odometer?: { min?: number; max?: number };
  /** Days since the unit arrived; the form collects months. */
  ageDays?: { min?: number; max?: number };
  conditions?: VehicleCondition[];
  /**
   * Membership dimensions rather than ranges: a non-empty list means the
   * unit's value must be one of these, an empty or absent list means "any".
   *
   * `businessUnits` matches the unit's own column. `clientIDs` matches the
   * client of the purchase order the unit was received on — a vehicle carries
   * no client of its own, so a unit received outside a PO never satisfies a
   * client list.
   */
  businessUnits?: BusinessUnit[];
  clientIDs?: number[];
}

export interface PackageRule {
  id?: string;
  criteria: PackageRuleCriteria;
}

/**
 * Dimension a form condition row targets. "Durasi Model" is captured in
 * months and stored as `ageDays` (TRD §4.2).
 */
export enum ConditionField {
  DURASI_MODEL = 'DURASI_MODEL',
  ODOMETER = 'ODOMETER',
  BUSINESS_UNIT = 'BUSINESS_UNIT',
  CLIENT = 'CLIENT',
}

export const CONDITION_FIELD_LABEL: Record<ConditionField, string> = {
  [ConditionField.DURASI_MODEL]: 'Durasi Model',
  [ConditionField.ODOMETER]: 'Odometer',
  [ConditionField.BUSINESS_UNIT]: 'Business Unit',
  [ConditionField.CLIENT]: 'Client',
};

/** Only the continuous dimensions carry a unit; membership rows have none. */
export const CONDITION_FIELD_UNIT: Partial<Record<ConditionField, string>> = {
  [ConditionField.DURASI_MODEL]: 'Bulan',
  [ConditionField.ODOMETER]: 'Km',
};

/**
 * Dimensions picked from a list instead of bounded by a number. They have no
 * operator (membership is neither `≥` nor `≤`) and no unit, so the row renders
 * a multi-select where a range row renders operator + value.
 *
 * Whatever this list holds is what the compile/decompile table in
 * `packageForm.ts` and the row editor's option table must cover — both are
 * keyed by `MembershipConditionField`, so adding a dimension here fails to
 * compile until it is handled in each.
 */
export const MEMBERSHIP_CONDITION_FIELDS = [
  ConditionField.BUSINESS_UNIT,
  ConditionField.CLIENT,
] as const;

export type MembershipConditionField =
  (typeof MEMBERSHIP_CONDITION_FIELDS)[number];

export const isMembershipConditionField = (
  field: ConditionField
): field is MembershipConditionField =>
  (MEMBERSHIP_CONDITION_FIELDS as readonly ConditionField[]).includes(field);

/** Days per month used to convert the "Durasi Model" input into `ageDays`. */
export const AGE_DAYS_PER_MONTH = 30;

/**
 * Only bounds are offered. TRD §4.2 rules out `=` on continuous dimensions
 * ("always a bug, never a feature"), so the design's `=` is not selectable.
 */
export enum ConditionOperator {
  GTE = 'GTE',
  LTE = 'LTE',
}

export const CONDITION_OPERATOR_LABEL: Record<ConditionOperator, string> = {
  [ConditionOperator.GTE]: '≥',
  [ConditionOperator.LTE]: '≤',
};

export interface PackageCondition {
  /** Client-side row key; not persisted. */
  key: string;
  field: ConditionField;
  /** Ignored on membership rows. */
  operator: ConditionOperator;
  /** The bound, on range rows only. */
  value: string;
  /**
   * The selected members, on membership rows only. Business units are enum
   * values, clients are provider ids as strings (the dropdown works in
   * strings; `compileCriteria` converts them back to numbers).
   */
  values: string[];
}

/** A free-day entry is a day-of-month, e.g. 15 and 30. */
export interface FreeDayDate {
  key: string;
  day: string;
}

// ---------------------------------------------------------------------------
// Content blocks (TRD C7)
// ---------------------------------------------------------------------------

export enum PackageContentType {
  BENEFIT = 'BENEFIT',
  TNC = 'TNC',
}

export interface PackageContentBlock {
  type: PackageContentType;
  title: string;
  /** Sanitized HTML from the rich text editors. */
  body: string;
}

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------

/** House envelope: `{ status, message?, data?, error?, errors? }`. */
export interface ApiEnvelope<T> {
  status: 'Success' | 'Failed';
  message?: string;
  data?: T;
  error?: unknown;
  errors?: unknown;
}

export interface ApiPagination {
  size: number;
  page: number;
  lastPage: number;
  total: number;
}

export interface Paginated<T> {
  items: T[];
  pagination: ApiPagination;
}

/** Row shape from `GET /packages`. Note: no deposit fields on the list. */
export interface PackageListItem {
  id: string;
  /** Some endpoints return uppercase ID (Drizzle PK casing) — normalised in
   *  the service layer, kept here so the raw response still type-checks. */
  ID?: string;
  name: string;
  description: string | null;
  imageURL: string | null;
  status: PackageStatus;
  contractType: RentType;
  visibility: PackageVisibility;
  heldBy: PackageHeldByList;
  model: { id: string; name: string; oemName: string };
  dailyPrice: number;
  activePreOrders: number;
  updatedAt?: string;
  updatedBy?: string;
}

/** Full shape from `GET /packages/:id`; also the POST/PUT response. */
export interface PackageDetail {
  id: string;
  /** See PackageListItem.ID. */
  ID?: string;
  name: string;
  description: string | null;
  /** Uploaded catalog artwork; null on packages saved before the upload existed. */
  imageURL: string | null;
  status: PackageStatus;
  modelID: string;
  contractType: RentType;
  /** Defaults to PUBLIC server-side when omitted on create. */
  visibility: PackageVisibility;
  /** Read-only; see PackageHeldByList. */
  heldBy: PackageHeldByList;
  dailyPrice: number;

  /** Total deposit (jaminan). */
  deposit: number;
  /** Portion Dash keeps however the rental ends (TRD C2). */
  depositNonRefundable: number;
  /** Minimum down payment at booking; may sit below the non-refundable part. */
  minDp: number;
  /** Daily instalment count settling the deposit remainder. */
  maxInstallments: number;

  /** Contract minimum — no return before this many days. */
  minRentalDays: number;
  /** Tenure before Free Days unlock — distinct from minRentalDays (TRD M7). */
  freeDayMinTenureDays: number;
  /** Recurring days-of-month, e.g. [15, 30]. */
  freeDayDates: number[];

  /**
   * Bookable pickup window, read-only from here. The design collects neither,
   * and the API owns their defaults (2 / 14) — see PackageWriteBody, which
   * omits both so the numbers are not duplicated client-side.
   */
  minBookingLeadDays: number;
  maxBookingLeadDays: number;
  dpPaymentDeadlineHours: number;
  /**
   * Read-only, and not settable from here.
   *
   * Previously flagged unresolved: the contract documents the field, while its
   * cancel note says any pre-handover exit *including NO_SHOW* refunds the DP
   * in full, which would leave forfeiture unreachable. The service settles it
   * — neither `CreatePackageRequestDto` nor `UpdatePackageRequestDto` declares
   * the property, and the API validates with `whitelist: true`, so a value
   * sent from here is stripped before it reaches the usecase rather than
   * rejected. Writing it is a silent no-op; the refund rule wins.
   */
  noShowDpForfeit: boolean;
  minReturnNoticeDays: number;
  maxPrepayDays: number;

  /** Max concurrent PRE_ORDER rentals; 0 disables pre-order. */
  maxPreorderCount: number;
  stockAlertThreshold: number;

  // lateShutdownDays / lateRepossessionDays are no longer per package: the
  // escalation thresholds are fleet-wide and live (Settings → Eskalasi Rent).

  rules: PackageRule[];
  content: PackageContentBlock[];

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * The editable half of a package — the detail shape minus id and timestamps.
 *
 * The contract describes the write body as "detail shape minus id/audit", but
 * the API in fact requires the actor: POST rejects a missing `createdBy` with
 * "createdBy must be a string". So the actor is added on top of this by the
 * request types below, and filled in by the service layer from the session.
 *
 * The booking-lead pair is omitted too: the API treats both as optional and
 * fills them from its own defaults on create, or keeps the stored value on
 * update. Sending them from here would mean maintaining the same numbers in
 * two codebases.
 */
export type PackageWriteBody = Omit<
  PackageDetail,
  | 'id'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'updatedBy'
  | 'minBookingLeadDays'
  | 'maxBookingLeadDays'
  | 'heldBy'
>;

/** POST /packages body. */
export type CreatePackageRequest = PackageWriteBody & { createdBy: string };

/** PUT /packages/:id body. */
export type UpdatePackageRequest = PackageWriteBody & { updatedBy: string };

/** Column the list is ordered by; the API defaults to `createdAt`. */
export type PackageSortField =
  'createdAt' | 'updatedAt' | 'name' | 'dailyPrice';

export interface GetPackagesParams {
  page?: number;
  size?: number;
  /** Defaults to `createdAt` server-side. */
  sort?: PackageSortField;
  /** Defaults to `desc` server-side. */
  order?: 'asc' | 'desc';
  status?: PackageStatus;
  contractType?: RentType;
  visibility?: PackageVisibility;
  modelID?: string;
  search?: string;
}

/** `GET /packages/:id/stock` — evaluated at request time (TRD P5). */
export interface PackageStock {
  available: number;
  activePreOrders: number;
  belowThreshold: boolean;
  /** RTO only; null on RENT. */
  tenorRangeDays: { min: number; max: number } | null;
  computedAt: string;
}

/**
 * Why a unit is not a package's stock (fleet #553). Gate codes hold whatever
 * the rules say; rule codes carry the OR-ed rule they fail; `*_UNKNOWN` sends
 * the whole range as `expected`.
 */
export type PackageVehicleReasonCode =
  | 'STATUS_NOT_IDLE'
  | 'PROCESS_NOT_AVAILABLE'
  | 'IS_BOOKING'
  | 'HELD_BY_OPEN_HANDOVER'
  | 'ODOMETER_UNKNOWN'
  | 'ODOMETER_BELOW_MIN'
  | 'ODOMETER_ABOVE_MAX'
  | 'AGE_UNKNOWN'
  | 'AGE_BELOW_MIN'
  | 'AGE_ABOVE_MAX'
  | 'CONDITION_NOT_ALLOWED'
  | 'BUSINESS_UNIT_NOT_ALLOWED'
  | 'CLIENT_NOT_ALLOWED'
  | 'NO_RULES';

export interface PackageVehicleReason {
  code: PackageVehicleReasonCode;
  ruleID?: string;
  expected?: number | { min?: number; max?: number } | (string | number)[];
  actual?: number | string | boolean | null;
}

/** One row of `GET /packages/:id/vehicles`. */
export interface PackageVehicle {
  id: string;
  /** Uppercase on the wire (Drizzle PK casing) — normalised to `id`. */
  ID?: string;
  platNomor: string;
  hubName: string | null;
  status: string;
  processType: string;
  isBooking: boolean;
  odometer: number | null;
  ageDays: number | null;
  condition: string;
  businessUnit: string | null;
  clientID: number | null;
  available: boolean;
  /** Set whenever a rule matches, even when the gate keeps the unit out. */
  matchedRuleID: string | null;
  /** Empty when available. */
  reasons: PackageVehicleReason[];
}

/**
 * `GET /packages/:id/vehicles` — every unit of the package's model except
 * RETURNED/RELEASED. `summary` is taken before search and the available
 * filter, and its `available` equals `GET /packages/:id/stock`.
 */
export interface PackageVehicles extends Paginated<PackageVehicle> {
  summary: {
    total: number;
    /**
     * Units passing the unit gate (Idle, Available, not booked, no open
     * handover) whatever the rules say: the stock preview's denominator.
     * Absent before fleet #556.
     */
    readyToRent?: number;
    available: number;
    unavailable: number;
  };
}

export interface GetPackageVehiclesParams {
  page: number;
  size: number;
  search?: string;
  available?: boolean;
}

/** `POST /packages/stock-preview/vehicles` — the list for unsaved criteria. */
export type PackageVehiclesPreviewRequest = PackageStockPreviewRequest &
  GetPackageVehiclesParams;

/**
 * `POST /packages/stock-preview` body — criteria that are not saved yet, so
 * the model has to travel with them (there is no package id to key off).
 */
export interface PackageStockPreviewRequest {
  modelID: string;
  criteria: PackageRuleCriteria;
}

/**
 * `POST /packages/stock-preview` — what the criteria being edited *would*
 * match right now, for the Condition editor. Advisory only: it is a live
 * count, not a reservation, so it can change before the package is saved.
 */
export interface PackageStockPreview {
  /** Available units of the model that satisfy the criteria. */
  available: number;
  /** Available units of the model, criteria ignored — the denominator. */
  totalOnModel: number;
  computedAt: string;
}

/**
 * `GET /packages/:id/history` — audit trail. Each row is a whole before/after
 * snapshot plus a reason, not a single field diff, so a reviewer can see the
 * package as it stood on either side of the edit (`package_history`).
 */
export interface PackageHistoryEntry {
  reason: string | null;
  before: Partial<PackageDetail> | null;
  after: Partial<PackageDetail> | null;
  createdBy: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------

/**
 * Everything the Create/Edit Package screen collects. Numbers stay strings
 * while editing; `packageForm.ts` maps to and from the API shapes.
 */
export interface PackageFormValues {
  /** "Package Name" — the API's free-text `name`, typed rather than picked. */
  name: string;
  /**
   * Temp-bucket URL after an upload, the stored URL in edit mode, `null` once
   * the user clears it (distinct from `undefined`, which the API reads as
   * "not sent"), `''` when there has never been one.
   */
  imageURL: string | null;
  /** "Variant Type" — the API's `contractType`. */
  type: RentType | '';
  /** "Visibilitas" — Publik (default) or Khusus. */
  visibility: PackageVisibility;
  modelID: string;
  /** "Harga Sewa per Hari (Rp)" — the API's `dailyPrice`. */
  dailyPrice: string;
  conditions: PackageCondition[];
  description: string;

  preOrderEnabled: boolean;
  /** "Max PO" — the API's `maxPreorderCount`. */
  maxPreorderCount: string;

  depositEnabled: boolean;
  /** "Deposit Total (Rp)" — the API's `deposit`. */
  deposit: string;
  /** The API's `depositNonRefundable`; see the form label note. */
  depositNonRefundable: string;

  downPaymentEnabled: boolean;
  /** "Down Payment (DP)" — the API's `minDp`. */
  minDp: string;
  /** "Cicilan Deposit" — the API's `maxInstallments`. */
  maxInstallments: string;

  freeDaysEnabled: boolean;
  /** Contract minimum (default 90). Not in the design; required by the API. */
  minRentalDays: string;
  /** "Minimal Masa Sewa (Hari)" — the API's `freeDayMinTenureDays`. */
  freeDayMinTenureDays: string;
  freeDayDates: FreeDayDate[];

  /** HTML from the rich text editors, mapped into `content`. */
  benefit: string;
  termsAndConditions: string;
}
